import React, { useCallback, useEffect, useRef, useState } from 'react';

import GroupingTab, {
  GROUPIG_TAB_PADDING,
  GROUPING_TAB_BORDER_WIDTH,
  GROUPING_TAB_WIDTH,
  normalTabStyle,
  TabStyleState,
} from './GroupingTab';
import ReachGroupLimitModal from '../modal/ReachGroupLimitModal';
import GroupingTutorialModal from '../modal/GroupingTutorialModal';
import {
  Asset,
  getCategorizedGroups,
  Group,
  GroupId,
  isUncategorizedId,
} from 'hooks/useGrouping';
import { usePrevious } from 'hooks/usePrevious';
import { Easing, Tween } from '@tweenjs/tween.js';
import { GroupDeletionState, GROUP_DELETION_STATE } from 'pages/Grouping';

const groupingUsedFlagKey = 'isAlreadyUsedGrouping';
export const isFirstUseGrouping = (
  isNotExistCategorizedGroup: boolean
): boolean => {
  const isGroupingUsed =
    Number(window.localStorage.getItem(groupingUsedFlagKey)) === 1;
  return !isGroupingUsed && isNotExistCategorizedGroup;
};

const firstUseGrouping = (): void => {
  window.localStorage.setItem(groupingUsedFlagKey, '1');
};

type GroupingTabListProps = {
  groups: Group[];
  assets: Asset[];
  currentGroup: Group;
  setCurrentGroup: (group: Group) => void;
  addGroup: () => void;
  groupClass: (group: Group) => string;
  isTabChanging: boolean;
  isScroll: boolean;
  setIsScroll: (value: boolean) => void;
  groupDeletionState: GroupDeletionState;
  onFinishGroupDeleteAnimation: () => void;
};

const MODAL = {
  REACH_GROUP_LIMIT: 'REACH_GROUP_LIMIT',
  GROUPING_TUTORIAL_MODAL: 'GROUPING_TUTORIAL_MODAL',
} as const;
type Modal = typeof MODAL[keyof typeof MODAL];

const GroupingTabList: React.FC<GroupingTabListProps> = props => {
  const { isScroll, setIsScroll } = props;
  const [modalToShow, setModalToShow] = useState<Modal | null>(null);
  const tabListContainerRef = useRef<HTMLUListElement>(null);
  const [scrollMarker, setScrollMarker] = useState<number | null>(null);
  const [isAnimateAddGroup, setIsAnimateAddGroup] = useState<boolean>(false);
  const prevActiveGroupId = usePrevious(props.currentGroup.id.id);
  const scrollMarkerRef = useCallback(
    (element: HTMLDivElement) => {
      if (!element) return;
      if (!isScroll && !isAnimateAddGroup) return;
      // elementのスクロール位置を取得
      const scrollMarginRight = 220;
      setScrollMarker(element.offsetLeft + scrollMarginRight);
    },
    [isScroll, isAnimateAddGroup]
  );
  useEffect(() => {
    // scrollする
    if (!scrollMarker) return;
    if (!tabListContainerRef.current) return;
    if (!isScroll) return;
    if (isAnimateAddGroup) return;
    const containerWidth = tabListContainerRef.current.clientWidth;
    const tweenObj = {
      to: tabListContainerRef.current.scrollLeft,
    };
    const nextTo = Math.max(scrollMarker - containerWidth, 0);
    const canScroll = nextTo !== 0;
    const scrollOffHandler = (e: Event) => e.preventDefault();
    new Tween(tweenObj)
      .to(
        {
          to: nextTo,
        },
        canScroll ? 700 : 0
      )
      .easing(Easing.Quadratic.Out)
      .onStart(() => {
        tabListContainerRef.current?.addEventListener(
          'mousewheel',
          scrollOffHandler,
          { passive: false }
        );
        tabListContainerRef.current?.addEventListener(
          'touchmove',
          scrollOffHandler,
          { passive: false }
        );
      })
      .onUpdate(() => {
        if (!tabListContainerRef.current) return;
        tabListContainerRef.current.scrollTo({
          left: tweenObj.to,
        });
      })
      .onComplete(() => {
        setScrollMarker(null);
        setIsScroll(false);
        tabListContainerRef.current?.removeEventListener(
          'mousewheel',
          scrollOffHandler
        );
        tabListContainerRef.current?.removeEventListener(
          'touchmove',
          scrollOffHandler
        );
      })
      .start();
  }, [
    scrollMarker,
    props.currentGroup.id.id,
    isScroll,
    isAnimateAddGroup,
    props.groups,
    setIsScroll,
  ]);

  const [tabStyleState, setTabStyleState] = useState<TabStyleState>({
    state: 'NORMAL',
    opacity: 1,
    padding: 8,
    width: 150,
    borderWidth: 1,
  });
  useEffect(() => {
    if (!tabListContainerRef.current) return;
    if (!isAnimateAddGroup) return;

    const tweenSize = {
      width: 0,
      borderWidth: 0,
      padding: 0,
    };
    const tweenOpacity = { opacity: 0 };
    const scrollOffHandler = (e: Event) => e.preventDefault();
    setTabStyleState({
      state: 'GROUP_ADD_ANIMATION',
      width: 0,
      padding: 0,
      opacity: 0,
      borderWidth: 0,
    });
    new Tween(tweenSize)
      .to(
        {
          width: GROUPING_TAB_WIDTH,
          padding: GROUPIG_TAB_PADDING,
          borderWidth: GROUPING_TAB_BORDER_WIDTH,
        },
        500
      )
      .easing(Easing.Quadratic.Out)
      .onStart(() => {
        tabListContainerRef.current?.addEventListener(
          'mousewheel',
          scrollOffHandler,
          { passive: false }
        );
        tabListContainerRef.current?.addEventListener(
          'touchmove',
          scrollOffHandler,
          { passive: false }
        );
      })
      .onUpdate(() => {
        setTabStyleState(prev => ({
          ...prev,
          state: 'GROUP_ADD_ANIMATION',
          padding: tweenSize.padding,
          width: tweenSize.width,
          borderWidth: tweenSize.borderWidth,
        }));
        if (!tabListContainerRef.current) return;
        tabListContainerRef.current.scrollBy({
          left:
            Math.round(tweenSize.width) +
            Math.round(tweenSize.padding) +
            Math.round(tweenSize.borderWidth),
        });
      })
      .chain(
        new Tween(tweenOpacity)
          .to({ opacity: 1 }, 200)
          .easing(Easing.Quadratic.Out)
          .onUpdate(() => {
            setTabStyleState(prev => ({
              ...prev,
              state: 'GROUP_ADD_ANIMATION',
              opacity: tweenOpacity.opacity,
            }));
          })
      )
      .onComplete(() => {
        setIsAnimateAddGroup(false);
        setTabStyleState({
          state: 'NORMAL',
          borderWidth: 1,
          opacity: 1,
          padding: 8,
          width: 150,
        });
        tabListContainerRef.current?.removeEventListener(
          'mousewheel',
          scrollOffHandler
        );
        tabListContainerRef.current?.removeEventListener(
          'touchmove',
          scrollOffHandler
        );
      })
      .start();
  }, [isAnimateAddGroup, props.groups]);

  useEffect(() => {
    // GroupDeletionStateがスタートしたときに、アニメーションを実行してアニメーション終了関数を呼び出す
    if (
      props.groupDeletionState !==
      GROUP_DELETION_STATE.GROUP_DELETE_ANIMATION_STARTED
    )
      return;
    const tweenOpacity = {
      opacity: 1,
    };
    const tweenSize = {
      width: GROUPING_TAB_WIDTH,
      borderWidth: GROUPING_TAB_BORDER_WIDTH,
      padding: GROUPIG_TAB_PADDING,
    };
    const tweenAllOpacity = {
      opacity: 1,
    };
    new Tween(tweenOpacity)
      .to({ opacity: 0 }, 200)
      .easing(Easing.Quadratic.Out)
      .onUpdate(() => {
        setTabStyleState(prev => ({
          ...prev,
          state: 'GROUP_DELETE_ANIMATION',
          opacity: tweenOpacity.opacity,
        }));
      })
      .chain(
        new Tween(tweenSize)
          .to(
            {
              width: 0,
              borderWidth: 0,
              padding: 0,
            },
            500
          )
          .easing(Easing.Quadratic.Out)
          .onUpdate(() => {
            setTabStyleState(prev => ({
              ...prev,
              state: 'GROUP_DELETE_ANIMATION',
              width: tweenSize.width,
              borderWidth: tweenSize.borderWidth,
              padding: tweenSize.padding,
            }));
          })
          .chain(
            new Tween(tweenAllOpacity)
              .to({ opacity: 0 }, 100)
              .easing(Easing.Quadratic.Out)
              .onUpdate(() => {
                setTabStyleState({
                  state: 'GROUP_DELETING',
                  allOpacity: tweenAllOpacity.opacity,
                  borderWidth: 0,
                  opacity: 0,
                  padding: 0,
                  width: 0,
                });
              })
              .onComplete(() => {
                props.onFinishGroupDeleteAnimation();
              })
          )
      )
      .start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.groupDeletionState]);

  useEffect(() => {
    if (props.groupDeletionState === GROUP_DELETION_STATE.GROUP_DELETE_READY)
      setTimeout(() => {
        setTabStyleState(normalTabStyle);
      }, 40);
  }, [props.groupDeletionState]);

  const getFilteredAsset = (groupId: GroupId): Asset[] =>
    props.assets.filter(asset => asset.groupId.id === groupId.id);
  const getGroupTotalAssetAmount = (groupId: GroupId): number =>
    getFilteredAsset(groupId)
      .map(asset => {
        if (asset.totalAssetAmount === '---') return 0;
        if (!asset.isTallyTarget) return 0;
        return asset.totalAssetAmount;
      })
      .reduce((prev, current) => prev + current, 0);
  const tabSelect = (group: Group) => {
    return (): void => props.setCurrentGroup(group);
  };
  const sortedGroups = props.groups.sort((gm, gn) => {
    if (isUncategorizedId(gm.id) && !isUncategorizedId(gn.id)) {
      return 1;
    } else if (!isUncategorizedId(gm.id) && isUncategorizedId(gn.id)) {
      return -1;
    } else {
      return 0;
    }
  });
  const addGroup = (): void => {
    if (getCategorizedGroups(props.groups).length >= 10) {
      setModalToShow(MODAL.REACH_GROUP_LIMIT);
    } else {
      props.addGroup();
      // グループを新規追加したときスクロールを調整する
      setIsAnimateAddGroup(true);
    }
  };
  const closeModal = (): void => {
    setModalToShow(null);
  };
  const isActive = (group: Group): boolean => {
    return props.currentGroup.id.id === group.id.id;
  };

  const groupAddButtonClickHandler = (): void => {
    if (isScroll || isAnimateAddGroup) return;
    if (isFirstUseGrouping(getCategorizedGroups(props.groups).length === 0)) {
      setModalToShow(MODAL.GROUPING_TUTORIAL_MODAL);
      firstUseGrouping();
    }
    addGroup();
  };

  const groupingTutorialModalCloseHandler = (): void => {
    closeModal();
  };

  return (
    <>
      <ul
        className={`grouping-tab-header ${props.groupClass(
          props.currentGroup
        )}`}
        ref={tabListContainerRef}>
        {sortedGroups.map((group, i, array) => {
          return (
            <React.Fragment key={group.id.id}>
              <GroupingTab
                group={group}
                groupTotalAssetAmount={getGroupTotalAssetAmount(group.id)}
                assetsCount={getFilteredAsset(group.id).length}
                isActive={isActive(group)}
                select={tabSelect(group)}
                groupClass={props.groupClass(group)}
                tabStyleState={
                  i === array.length - 2 &&
                  tabStyleState.state === 'GROUP_ADD_ANIMATION'
                    ? tabStyleState
                    : isActive(group) &&
                      tabStyleState.state === 'GROUP_DELETE_ANIMATION' &&
                      props.groupDeletionState ===
                        'GROUP_DELETE_ANIMATION_STARTED'
                    ? tabStyleState
                    : tabStyleState.state === 'GROUP_DELETING' &&
                      prevActiveGroupId === group.id.id
                    ? tabStyleState
                    : normalTabStyle
                }
              />
              {isActive(group) && (
                <div
                  key={group.id.id}
                  ref={scrollMarkerRef}
                  style={{ width: 0, height: 0 }}
                />
              )}
            </React.Fragment>
          );
        })}
        <li
          className="grouping-tab-add cursor-pointer"
          onClick={groupAddButtonClickHandler}>
          <div className="btn-add icon-plus"></div>
        </li>
      </ul>
      <ReachGroupLimitModal
        isShow={modalToShow === MODAL.REACH_GROUP_LIMIT}
        close={closeModal}
      />
      <GroupingTutorialModal
        isShow={modalToShow === MODAL.GROUPING_TUTORIAL_MODAL}
        close={groupingTutorialModalCloseHandler}
      />
    </>
  );
};

export default GroupingTabList;
